// ============================================
// AlpenScreen - Toast Notification
// File: frontend/src/components/Toast.jsx
// Purpose: Show notifications for device connections, etc.
// ============================================

import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, Monitor } from 'lucide-react';

const Toast = ({ message, type = 'info', duration = 5000, onClose }) => {
    console.log('🎨 Toast RENDERING:', { message, type, duration }); // ← ADD THIS

  useEffect(() => {
    console.log('⏱️ Toast mounted, setting timer:', duration); // ← ADD THIS
    if (duration > 0) {
      const timer = setTimeout(() => {
        console.log('⏱️ Toast timer expired, closing'); // ← ADD THIS
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const icons = {
    success: <CheckCircle className="w-5 h-5" />,
    error: <AlertCircle className="w-5 h-5" />,
    info: <Info className="w-5 h-5" />,
    device: <Monitor className="w-5 h-5" />
  };

  const styles = {
    success: 'bg-green-50 border-green-200 text-green-900',
    error: 'bg-red-50 border-red-200 text-red-900',
    info: 'bg-blue-50 border-blue-200 text-blue-900',
    device: 'bg-purple-50 border-purple-200 text-purple-900'
  };

  const iconStyles = {
    success: 'text-green-600',
    error: 'text-red-600',
    info: 'text-blue-600',
    device: 'text-purple-600'
  };

  return (
    <div
      className={`
        fixed top-4 right-4 z-[9999]
        flex items-start gap-3 p-4 pr-12
        border-2 rounded-xl shadow-2xl
        min-w-[320px] max-w-md
        animate-slide-in
        ${styles[type]}
      `}
    >
      <div className={iconStyles[type]}>
        {icons[type]}
      </div>
      
      <div className="flex-1">
        <p className="font-semibold mb-1">{message.title}</p>
        {message.description && (
          <p className="text-sm opacity-90">{message.description}</p>
        )}
        {message.action && (
          <button
            onClick={message.action.onClick}
            className="mt-2 text-sm font-semibold underline hover:no-underline"
          >
            {message.action.label}
          </button>
        )}
      </div>

      <button
        onClick={onClose}
        className="absolute top-2 right-2 p-1 rounded-lg hover:bg-black/5 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Container
export const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-0 right-0 z-[9999] pointer-events-none">
      <div className="flex flex-col gap-2 p-4 pointer-events-auto">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Toast Hook
export const useToast = () => {
  const [toasts, setToasts] = React.useState([]);

  const addToast = React.useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = React.useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
};

export default Toast;